import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {alpha, useTheme} from "@mui/material/styles";
import type {ColumnDef} from "@tanstack/react-table";
import type {
    BirthdayEvent,
    CancelEvent,
    ProlongEvent,
    ReservationDateTimeMeta,
    TableCancelGroup,
} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {FloorplanService} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {
    convertLocalDateToDate,
    endOfDay,
    formatDateLocalISO,
    formatDateRangePretty,
} from "crqlar-react-common/dist/utils/date.util";
import {getProfileFullName} from "crqlar-react-common/dist/utils/profile.util";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ElementChip} from "../common/uiElements/base/ElementChip";
import {AccommodationElement, RestaurantElement} from "../common/uiElements/ResourceElement";
import {StayRangeElement} from "../common/uiElements/StayRangeElement";
import {
    ServerTable,
    SERVER_TABLE_EXPANSION_COLUMN_WIDTH,
    ServerTableFrame,
    ServerTableProvider,
    useServerTable,
} from "../common/serverTable";
import EventsCancelDialog from "../reservations/restaurantActionCenter/EventsCancelDialog";
import EventsUpdateDialog from "../reservations/restaurantActionCenter/EventsUpdateDialog";

type RestaurantDailyEventKind = "prolong" | "cancel" | "birthday";

interface RestaurantDailyEventRow {
    key: string;
    kind: RestaurantDailyEventKind;
    restaurantId: string;
    restaurantLabel: string;
    profileId?: string;
    guestLabel: string;
    roomLabel?: string;
    stayStart?: Date;
    stayEnd?: Date;
    accommodationReservation?: Reservation;
    prolongEvent?: ProlongEvent;
    cancelEvent?: CancelEvent;
    cancelByTable?: TableCancelGroup[];
    profile?: Profile;
}

type ActionDialogSelection =
    | {kind: "prolong"; row: RestaurantDailyEventRow; event: ProlongEvent}
    | {kind: "cancel"; row: RestaurantDailyEventRow; event: CancelEvent};

interface RestaurantDailyEventsProps {
    selectedDate: Date;
    restaurants: readonly Resource[];
    alignGuestColumnWithExpansion: boolean;
    onEventCountChange: (count: number | undefined) => void;
    onOpenReservation: (reservation: Reservation, profile?: Profile) => void;
}

const getStayDates = (
    metas?: ReservationDateTimeMeta[],
    reservations?: Reservation[],
) => {
    const reservation = reservations?.[0];
    const meta = metas?.[0];
    const start = reservation?.actualStartDate
        ?? reservation?.expectedStartDate
        ?? convertLocalDateToDate(
            reservation?.actualStartDay ?? reservation?.expectedStartDay ?? meta?.startDay ?? undefined,
        );
    const end = reservation?.actualEndDate
        ?? reservation?.expectedEndDate
        ?? convertLocalDateToDate(
            reservation?.actualEndDay ?? reservation?.expectedEndDay ?? meta?.endDay ?? undefined,
        );

    return {
        stayStart: start ?? undefined,
        stayEnd: end ?? undefined,
    };
};

export default function RestaurantDailyEvents({
    selectedDate,
    restaurants,
    alignGuestColumnWithExpansion,
    onEventCountChange,
    onOpenReservation,
}: RestaurantDailyEventsProps) {
    const {t, i18n} = useTranslation();
    const restaurantColorById = useMemo(() => new Map(
        restaurants.flatMap(resource => resource.id
            ? [[resource.id, resource.meta?.tagColor?.trim() || undefined] as const]
            : []),
    ), [restaurants]);
    const theme = useTheme();
    const [actionDialog, setActionDialog] = useState<ActionDialogSelection>();
    const [loadingActionRowKey, setLoadingActionRowKey] = useState<string>();
    const locale = i18n.resolvedLanguage || i18n.language;
    const restaurantIds = useMemo(
        () => restaurants.map(resource => resource.id).filter((id): id is string => Boolean(id)).sort(),
        [restaurants],
    );
    const restaurantSelectionKey = useMemo(
        () => restaurants
            .map(resource => `${resource.id ?? ""}:${resource.name ?? ""}`)
            .sort()
            .join("|"),
        [restaurants],
    );
    const selectedRestaurantIdSet = useMemo(() => new Set(restaurantIds), [restaurantIds]);
    const restaurantNameById = useMemo(
        () => new Map(restaurants.flatMap(resource => resource.id
            ? [[resource.id, resource.name || t("Restaurant")] as const]
            : [])),
        [restaurants, t],
    );
    const guestColumnInset = alignGuestColumnWithExpansion
        ? `${SERVER_TABLE_EXPANSION_COLUMN_WIDTH}px`
        : 0;

    const handleActionClick = useCallback((row: RestaurantDailyEventRow) => {
        if (loadingActionRowKey) return;
        if (row.kind === "prolong" && row.prolongEvent) {
            setLoadingActionRowKey(row.key);
            setActionDialog({kind: "prolong", row, event: row.prolongEvent});
        }
        if (row.kind === "cancel" && row.cancelEvent) {
            setLoadingActionRowKey(row.key);
            setActionDialog({kind: "cancel", row, event: row.cancelEvent});
        }
    }, [loadingActionRowKey]);

    const handleOpenRowReservation = useCallback(async (row: RestaurantDailyEventRow) => {
        if (row.accommodationReservation) {
            onOpenReservation(row.accommodationReservation, row.profile);
            return;
        }
        if (row.kind !== "birthday" || !row.profileId) return;

        try {
            const response = await ReservationService.getAllReservations({
                profileIds: [row.profileId],
                resourceIds: [row.restaurantId],
                overlaps: {start: selectedDate, end: endOfDay(selectedDate)},
                status: [
                    ReservationStatus.PENDING,
                    ReservationStatus.CONFIRMED,
                    ReservationStatus.ACTIVE,
                    ReservationStatus.OPTION,
                    ReservationStatus.UNCOMPLETED,
                ],
                limit: {
                    offset: 0,
                    limit: 1,
                    sort: [{field: "expectedStartDate", direction: "ASC"}],
                },
            });
            const reservation = response.content?.[0];
            if (reservation) onOpenReservation(reservation, row.profile);
        } catch (error) {
            console.error(error);
        }
    }, [onOpenReservation, selectedDate]);

    const getEventDisplay = useCallback((kind: RestaurantDailyEventKind) => {
        switch (kind) {
            case "prolong":
                return {
                    label: t("Prolonged stay"),
                    icon: <HotelOutlinedIcon sx={{fontSize: 15, color: theme.palette.info.dark}}/>,
                    borderColor: alpha(theme.palette.info.main, 0.34),
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                };
            case "cancel":
                return {
                    label: t("Early departure"),
                    icon: <WarningAmberOutlinedIcon sx={{fontSize: 15, color: theme.palette.error.dark}}/>,
                    borderColor: alpha(theme.palette.error.main, 0.34),
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                };
            case "birthday":
                return {
                    label: t("Birthday"),
                    icon: <CakeOutlinedIcon sx={{fontSize: 15, color: theme.palette.success.dark}}/>,
                    borderColor: alpha(theme.palette.success.main, 0.34),
                    backgroundColor: alpha(theme.palette.success.main, 0.08),
                };
        }
    }, [t, theme]);

    const columns = useMemo<ColumnDef<RestaurantDailyEventRow>[]>(() => [
        {
            id: "guest",
            header: () => (
                <Box component="span" sx={{display: "block", pl: guestColumnInset}}>
                    {t("Guest")}
                </Box>
            ),
            accessorFn: row => row.guestLabel,
            cell: ({row}) => {
                const canOpenReservation = Boolean(row.original.accommodationReservation)
                    || (row.original.kind === "birthday" && Boolean(row.original.profileId));

                return (
                    <ButtonBase
                        disabled={!canOpenReservation}
                        onClick={() => void handleOpenRowReservation(row.original)}
                        sx={{
                            justifyContent: "flex-start",
                            minWidth: 0,
                            maxWidth: "100%",
                            pl: guestColumnInset,
                            borderRadius: 0.5,
                            textAlign: "left",
                            "&.Mui-disabled": {color: "text.primary"},
                        }}
                    >
                        <Typography variant="body1" noWrap>
                            {row.original.guestLabel}
                        </Typography>
                    </ButtonBase>
                );
            },
            size: 220,
            meta: {grow: true},
        },
        {
            id: "restaurant",
            header: t("Restaurant"),
            accessorFn: row => row.restaurantLabel,
            cell: ({row}) => (
                <RestaurantElement
                    name={row.original.restaurantLabel}
                    color={restaurantColorById.get(row.original.restaurantId)}
                />
            ),
            size: 220,
        },
        {
            id: "room",
            header: t("Room"),
            accessorFn: row => row.roomLabel,
            cell: ({row}) => row.original.roomLabel
                ? <AccommodationElement name={row.original.roomLabel}/>
                : null,
            size: 110,
        },
        {
            id: "stayRange",
            header: t("Stay range"),
            accessorFn: row => row.stayStart,
            cell: ({row}) => (
                <StayRangeElement
                    start={row.original.stayStart}
                    end={row.original.stayEnd}
                    locale={locale}
                />
            ),
            size: 180,
        },
        {
            id: "event",
            header: t("Events"),
            accessorFn: row => row.kind,
            cell: ({row}) => {
                const display = getEventDisplay(row.original.kind);
                return (
                    <ElementChip
                        text={display.label}
                        startIcon={display.icon}
                        borderColor={display.borderColor}
                        backgroundColor={display.backgroundColor}
                    />
                );
            },
            size: 200,
        },
        {
            id: "action",
            header: "",
            cell: ({row}) => {
                const actionLabel = row.original.kind === "prolong"
                    ? t("Update")
                    : row.original.kind === "cancel" ? t("Cancel") : undefined;
                if (!actionLabel) return null;

                const actionLoading = loadingActionRowKey === row.original.key;
                return (
                    <Button
                        size="small"
                        variant="text"
                        color={row.original.kind === "cancel" ? "error" : "primary"}
                        disabled={Boolean(loadingActionRowKey)}
                        onClick={() => handleActionClick(row.original)}
                        startIcon={actionLoading
                            ? <CircularProgress color="inherit" size={14}/>
                            : undefined}
                        sx={{minWidth: 0}}
                    >
                        {actionLabel}
                    </Button>
                );
            },
            size: 110,
            meta: {align: "right"},
        },
    ], [getEventDisplay, guestColumnInset, handleActionClick, handleOpenRowReservation, loadingActionRowKey, locale, restaurantColorById, t]);

    const controller = useServerTable<RestaurantDailyEventRow>({
        tableId: "restaurant-daily-events",
        columns,
        getRowId: row => row.key,
        defaultPageSize: 1000,
        queryDependencies: [restaurantSelectionKey, selectedDate.getTime()],
        queryFn: async () => {
            if (!restaurantIds.length) return {content: [], totalElements: 0};

            const response = await FloorplanService.listEvents({
                restaurantIds,
                date: formatDateLocalISO(selectedDate) ?? "",
            });
            const groups = (response.byRestaurant ?? []).filter(group => (
                Boolean(group.restaurantId) && selectedRestaurantIdSet.has(group.restaurantId!)
            ));
            const accommodationResourceIds = Array.from(new Set(groups.flatMap(group => [
                ...(group.prolong ?? []).flatMap(event => (
                    (event.accommodationReservationsMeta ?? []).flatMap(meta => meta.resourceIds ?? [])
                )),
                ...(group.cancel ?? []).flatMap(event => (
                    (event.accommodationReservationsMeta ?? []).flatMap(meta => meta.resourceIds ?? [])
                )),
            ]))).sort();
            const accommodationResources = accommodationResourceIds.length
                ? (await ResourceService.getAllResources({
                    id: accommodationResourceIds,
                    limit: {
                        offset: 0,
                        limit: 1000,
                        sort: [{field: "name", direction: "ASC"}],
                    },
                })).content ?? []
                : [];
            const accommodationResourceById = new Map(
                accommodationResources.map(resource => [resource.id, resource]),
            );
            const getProfileName = (event: ProlongEvent | BirthdayEvent | CancelEvent) => (
                getProfileFullName(event._profile ?? undefined) || event.profileId || t("Guest")
            );
            const getRoomLabel = (
                metas?: ReservationDateTimeMeta[],
                reservations?: Reservation[],
            ) => {
                const resourceIds = reservations?.[0]?.resourceIds ?? metas?.[0]?.resourceIds;
                return resourceIds
                    ?.map(id => accommodationResourceById.get(id)?.name)
                    .filter(Boolean)
                    .join(", ") || undefined;
            };
            const getBirthdayAge = (event: BirthdayEvent) => {
                if (event.turningAge !== null && event.turningAge !== undefined) return event.turningAge;
                const birthDate = convertLocalDateToDate(event._profile?.birthDay);
                if (!birthDate) return undefined;

                let age = selectedDate.getFullYear() - birthDate.getFullYear();
                const birthdayThisYear = new Date(
                    selectedDate.getFullYear(),
                    birthDate.getMonth(),
                    birthDate.getDate(),
                );
                if (selectedDate.getTime() < birthdayThisYear.getTime()) age -= 1;
                return age >= 0 ? age : undefined;
            };

            const rows = groups.flatMap(group => {
                const restaurantId = group.restaurantId!;
                const restaurantLabel = restaurantNameById.get(restaurantId)
                    || group._restaurant?.name
                    || t("Restaurant");
                const prolongRows = (group.prolong ?? []).map((event, index): RestaurantDailyEventRow => ({
                    key: `${restaurantId}-prolong-${event.profileId}-${index}`,
                    kind: "prolong",
                    restaurantId,
                    restaurantLabel,
                    profileId: event.profileId,
                    guestLabel: getProfileName(event),
                    roomLabel: getRoomLabel(event.accommodationReservationsMeta, event._accommodationReservations),
                    ...getStayDates(event.accommodationReservationsMeta, event._accommodationReservations),
                    accommodationReservation: event._accommodationReservations?.[0],
                    prolongEvent: event,
                    profile: event._profile ?? undefined,
                }));
                const cancelRows = (group.cancel ?? []).map((event, index): RestaurantDailyEventRow => ({
                    key: `${restaurantId}-cancel-${event.profileId}-${index}`,
                    kind: "cancel",
                    restaurantId,
                    restaurantLabel,
                    profileId: event.profileId,
                    guestLabel: getProfileName(event),
                    roomLabel: getRoomLabel(event.accommodationReservationsMeta, event._accommodationReservations),
                    ...getStayDates(event.accommodationReservationsMeta, event._accommodationReservations),
                    accommodationReservation: event._accommodationReservations?.[0],
                    cancelEvent: event,
                    cancelByTable: (group.cancelByTable ?? [])
                        .map(tableGroup => ({
                            ...tableGroup,
                            orphans: tableGroup.orphans.filter(orphan => orphan.profileId === event.profileId),
                        }))
                        .filter(tableGroup => tableGroup.orphans.length > 0),
                    profile: event._profile ?? undefined,
                }));
                const birthdayRows = (group.birthday ?? []).map((event, index): RestaurantDailyEventRow => {
                    const guestLabel = getProfileName(event);
                    const age = getBirthdayAge(event);
                    return {
                        key: `${restaurantId}-birthday-${event.profileId}-${index}`,
                        kind: "birthday",
                        restaurantId,
                        restaurantLabel,
                        profileId: event.profileId,
                        guestLabel: age === undefined ? guestLabel : `${guestLabel} (${age})`,
                        profile: event._profile ?? undefined,
                    };
                });

                return [...prolongRows, ...cancelRows, ...birthdayRows];
            });

            return {content: rows, totalElements: rows.length};
        },
    });

    useEffect(() => {
        if (controller.query.isLoading || controller.query.isFetching || controller.query.isError) {
            onEventCountChange(undefined);
            return;
        }
        onEventCountChange(controller.query.data?.totalElements ?? 0);
    }, [
        controller.query.data?.totalElements,
        controller.query.isError,
        controller.query.isFetching,
        controller.query.isLoading,
        onEventCountChange,
    ]);

    const handleActionDialogReady = useCallback(() => {
        setLoadingActionRowKey(undefined);
    }, []);
    const closeActionDialog = useCallback(() => {
        setActionDialog(undefined);
        setLoadingActionRowKey(undefined);
    }, []);
    const updateDialog = actionDialog?.kind === "prolong" ? actionDialog : undefined;
    const cancelDialog = actionDialog?.kind === "cancel" ? actionDialog : undefined;
    const actionDialogStayLabel = actionDialog?.row.stayStart && actionDialog.row.stayEnd
        ? formatDateRangePretty(actionDialog.row.stayStart, actionDialog.row.stayEnd, locale)
        : undefined;
    const showEmptyState = !controller.query.isLoading
        && !controller.query.isFetching
        && !controller.query.isError
        && controller.query.data?.totalElements === 0;

    return (
        <>
            <Box
                id="restaurant-daily-events"
                sx={{
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                {showEmptyState ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                        sx={{height: 200, color: "text.secondary", textAlign: "center"}}
                    >
                        <EventBusyOutlinedIcon/>
                        <Typography variant="body1" sx={{fontWeight: 700, color: "text.primary"}}>
                            {t("There are no events on this day")}
                        </Typography>
                    </Stack>
                ) : (
                    <ServerTableProvider controller={controller}>
                        <ServerTableFrame>
                            <ServerTable
                                stickyHeader={false}
                                layout="content"
                                showPagination={false}
                            />
                        </ServerTableFrame>
                    </ServerTableProvider>
                )}
            </Box>

            <EventsUpdateDialog
                open={Boolean(updateDialog)}
                event={updateDialog?.event}
                guestLabel={updateDialog?.row.guestLabel}
                accommodationStayLabel={actionDialogStayLabel}
                restaurantResourceId={updateDialog?.row.restaurantId}
                onOpenReservation={onOpenReservation}
                onOpenAccommodationReservation={onOpenReservation}
                onBodyReady={handleActionDialogReady}
                onClose={closeActionDialog}
            />
            <EventsCancelDialog
                open={Boolean(cancelDialog)}
                event={cancelDialog?.event}
                cancelByTable={cancelDialog?.row.cancelByTable ?? []}
                guestLabel={cancelDialog?.row.guestLabel}
                accommodationStayLabel={actionDialogStayLabel}
                restaurantLabel={cancelDialog?.row.restaurantLabel}
                onReservationsChanged={() => void controller.query.refetch()}
                onOpenReservation={onOpenReservation}
                onOpenAccommodationReservation={onOpenReservation}
                onBodyReady={handleActionDialogReady}
                onClose={closeActionDialog}
            />
        </>
    );
}
